// src/pages/signagedisplaypage.js
// Passive Signage Display Page — for unattended FullPageOS Chromium kiosks (e.g. DUTTO).
//
// Wires three auth paths, in order:
//   1. ?dt=<deviceToken> from URL → POST /apitest/api/device-token (Lane C, future)
//   2. localStorage admin session → load existing accessToken / refreshToken
//   3. Refresh proactively 5 min before access-token exp (Lane B, local).
//
// Then polls GET /apitest/signage/active every 60 s. Re-mounts <CampaignPlayer/>
// when the campaign id or slide count changes.
//
// Q3 (per design doc): content-only — no overlay, no debug UI on the campaign.
// Q5: empty state = branded DynamicSunLogo on black.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import CampaignPlayer from '../components/signage/CampaignPlayer';
import DynamicSunLogo from '../components/DynamicSunLogo';
import {
    getActiveCampaign,
    getMediaUrl,
} from '../services/signageService';
import { NODE_BACKEND_API_URL } from '../config';
import './signagedisplaypage.css';

const REFRESH_LEEWAY_MS = 5 * 60 * 1000;     // refresh 5 min before access-token exp
const POLL_INTERVAL_MS  = 60 * 1000;         // poll 60 s
const TICK_INTERVAL_MS  = 30 * 1000;         // token-expiry re-check 30 s

// Read-only base64url decode, for inspecting the JWT's `exp` claim without verifying.
function jwtExp(token) {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const padded = payload + '==='.slice((payload.length + 3) % 4);
        const obj = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof obj.exp === 'number' ? obj.exp * 1000 : null;
    } catch {
        return null;
    }
}

// LANE B (local) — returns true if access token will expire within leeway.
function accessTokenExpiringSoon(token, leewayMs) {
    const exp = jwtExp(token);
    if (!exp) return false;
    return Date.now() + leewayMs >= exp;
}

// Reads the device token from the URL, clears it, returns it.
// Returns null if absent or malformed. Reads `?dt=` either from
// `window.location.hash` (kiosk URLs of the form `#/signage/display?dt=...`)
// or from `window.location.search` (kiosk URLs of the form
// `/signage/display?dt=...` — used when the URL has no `#` fragment so it
// survives the Chromium `--app=URL` argv parsing intact).
function captureDeviceTokenFromHash() {
    try {
        // First, try the query-string on the path itself.
        const searchParams = new URLSearchParams(window.location.search || '');
        let dt = searchParams.get('dt');
        let source = 'search';

        // Fall back to inside the hash fragment, e.g. "#/signage/display?dt=..."
        if (!dt) {
            const hash = window.location.hash || '';
            const queryIdx = hash.indexOf('?');
            if (queryIdx !== -1) {
                const inner = new URLSearchParams(hash.slice(queryIdx + 1));
                dt = inner.get('dt');
                source = 'hash';
            }
        }
        if (!dt) return null;

        // Strip `dt` from whichever side carried it, leaving the URL clean.
        if (source === 'search') {
            const u = new URL(window.location.href);
            u.searchParams.delete('dt');
            window.history.replaceState(null, '', u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '') + u.hash);
        } else {
            const hash = window.location.hash || '';
            const queryIdx = hash.indexOf('?');
            const qs = hash.slice(queryIdx + 1);
            const params = new URLSearchParams(qs);
            params.delete('dt');
            const remaining = params.toString();
            const newHash = hash.slice(0, queryIdx) + (remaining ? '?' + remaining : '');
            if (newHash !== hash) {
                window.history.replaceState(null, '', window.location.pathname + newHash);
            }
        }
        return dt;
    } catch {
        return null;
    }
}

const AuthState = Object.freeze({
    BOOTING:   'BOOTING',
    OK:        'OK',
    EMPTY:     'EMPTY',
    NEEDS_REAUTH: 'NEEDS_REAUTH',
});

function SignageDisplayPage() {
    const [authState, setAuthState] = useState(AuthState.BOOTING);
    const [activeCampaign, setActiveCampaign] = useState(null);
    const [deviceTokenInput, setDeviceTokenInput] = useState('');
    const pollRef = useRef(null);

    // === PHASE: token mgmt helpers (Lane B + Lane C stubs) =========================
    const saveLocalAuth = useCallback((accessToken, refreshToken, user) => {
        if (accessToken)  localStorage.setItem('authAccessToken', accessToken);
        if (refreshToken) localStorage.setItem('authRefreshToken', refreshToken);
        if (user)         localStorage.setItem('authUser', JSON.stringify(user));
    }, []);
    const readLocalAccess  = useCallback(() => localStorage.getItem('authAccessToken'), []);
    const readLocalRefresh = useCallback(() => localStorage.getItem('authRefreshToken'), []);

    const tryRefresh = useCallback(async (refreshToken) => {
        try {
            // Flask: replace /login with /refresh in the existing config URL.
            const refreshUrl = NODE_BACKEND_API_URL
                ? NODE_BACKEND_API_URL.replace(/\/+$/, '')
                : '';
            // The Flask endpoint is at /flaskapi/auth/refresh via nginx; the Node
            // LoginProvider uses NODE_FLASK_LOG_URL. If that's not wired, fall back
            // to a relative call. SignageDisplayPage intentionally doesn't import
            // NODE_FLASK_LOG_URL to avoid pulling auth-prod globals; admin web uses
            // the AuthProvider interceptor for the same flow. Keep this best-effort.
            const url = (window.__FLASK_REFRESH_URL__ || '/flaskapi/auth/refresh');
            const resp = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken, rotateToken: false }),
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            if (!data.accessToken && !data.token) return null;
            const next = data.accessToken || data.token;
            saveLocalAuth(next, data.refreshToken || refreshToken, data.user);
            return next;
        } catch {
            return null;
        }
    }, [saveLocalAuth]);

    const exchangeDeviceToken = useCallback(async (deviceToken) => {
        try {
            const resp = await fetch('/apitest/api/device-token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deviceToken }),
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            if (!data.accessToken) return null;
            saveLocalAuth(
                data.accessToken,
                data.refreshToken || null,
                data.user || null
            );
            return data.accessToken;
        } catch {
            return null;
        }
    }, [saveLocalAuth]);

    // === PHASE: mount auth =========================================================
    useEffect(() => {
        let cancelled = false;
        (async () => {
            // 1. try device token from URL (Lane C)
            const dt = captureDeviceTokenFromHash();
            if (dt) {
                const tok = await exchangeDeviceToken(dt);
                if (!cancelled && tok) return setAuthState(AuthState.OK);
                // fall through to local session — dt may have just been wrong
            }
            // 2. try existing local session
            const access = readLocalAccess();
            const refresh = readLocalRefresh();
            if (!access) return setAuthState(AuthState.NEEDS_REAUTH);

            // 3. proactive refresh if token will expire within leeway
            if (accessTokenExpiringSoon(access, REFRESH_LEEWAY_MS)) {
                if (!refresh) return setAuthState(AuthState.NEEDS_REAUTH);
                const ok = await tryRefresh(refresh);
                if (cancelled) return;
                if (!ok) return setAuthState(AuthState.NEEDS_REAUTH);
            }
            setAuthState(AuthState.OK);
        })();
        return () => { cancelled = true; };
    }, []);

    // === PHASE: 60s polling loop ===================================================
    useEffect(() => {
        if (authState !== AuthState.OK && authState !== AuthState.EMPTY) return;
        let cancelled = false;
        let lastSignature = null;

        async function tick() {
            try {
                // getActiveCampaign() in signageService.js returns response.data.campaign
                // (the unwrapped campaign object, or null when the server has no schedule
                // for today). It does NOT return the full {campaign: ...} envelope.
                const campaign = await getActiveCampaign();
                if (cancelled) return;
                // === diagnostics ===
                try {
                    console.log('[signage] getActiveCampaign returned:', {
                        isObject: !!(campaign && typeof campaign === 'object'),
                        id: campaign && campaign.id,
                        slideCount: campaign && campaign.slides ? campaign.slides.length : 0,
                        orientation: campaign && campaign.orientation,
                        rawStringified: (() => { try { return JSON.stringify(campaign).slice(0, 200); } catch (_) { return '<unstringifiable>'; } })(),
                    });
                } catch (_) { /* ignore logging errors */ }
                const sig = campaign ? `${campaign.id}:${(campaign.slides || []).length}` : 'none';
                if (sig !== lastSignature) {
                    lastSignature = sig;
                    setActiveCampaign(campaign);
                    setAuthState(campaign ? AuthState.OK : AuthState.EMPTY);
                    console.log('[signage] setActiveCampaign id=' + (campaign ? campaign.id : null) +
                        ' slides=' + (campaign && campaign.slides ? campaign.slides.length : 0));
                }
            } catch (e) {
                console.error('[signage] getActiveCampaign threw:', e && e.message, e && e.response && e.response.status);
                if (e && e.response && e.response.status === 401) {
                    if (!cancelled) setAuthState(AuthState.NEEDS_REAUTH);
                }
            }
        }

        tick();
        pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [authState]);

    // === PHASE: token-expiry check (Lane B, local) =================================
    useEffect(() => {
        if (authState !== AuthState.OK && authState !== AuthState.EMPTY) return;
        const h = setInterval(async () => {
            const access = readLocalAccess();
            const refresh = readLocalRefresh();
            if (!access) {
                setAuthState(AuthState.NEEDS_REAUTH);
                return;
            }
            if (accessTokenExpiringSoon(access, REFRESH_LEEWAY_MS) && refresh) {
                const ok = await tryRefresh(refresh);
                if (!ok) setAuthState(AuthState.NEEDS_REAUTH);
            }
        }, TICK_INTERVAL_MS);
        return () => clearInterval(h);
    }, [authState, readLocalAccess, readLocalRefresh, tryRefresh]);

    // === PHASE: kiosk hardening ====================================================
    useEffect(() => {
        const blockRightClick = (e) => e.preventDefault();
        const blockKeys = (e) => {
            if (e.key === 'F5' || e.key === 'F11' || e.key === 'Escape') e.preventDefault();
            if ((e.ctrlKey && (e.key === 'r' || e.key === 'R' || e.key === 'w' || e.key === 'W'))
                || (e.altKey && e.key === 'F4')) {
                e.preventDefault();
            }
        };
        document.addEventListener('contextmenu', blockRightClick);
        document.addEventListener('keydown', blockKeys);
        return () => {
            document.removeEventListener('contextmenu', blockRightClick);
            document.removeEventListener('keydown', blockKeys);
        };
    }, []);

    // === Render ===================================================================
    // All four returns are wrapped in `.signage-display-root` so the kiosk-only
    // CSS (cursor-hide, fixed positioning, etc.) stays scoped to this page.
    // The class is unused on the admin / Buscador / Dashboard pages.
    if (authState === AuthState.BOOTING) {
        return (
            <div className="signage-display-root">
                <div className="signage-display-empty">
                    <DynamicSunLogo size={200} />
                </div>
            </div>
        );
    }

    if (authState === AuthState.NEEDS_REAUTH) {
        return (
            <div className="signage-display-root">
                <div className="signage-display-empty">
                    <DynamicSunLogo size={200} />
                    <div className="signage-display-reauth">
                        <div className="signage-display-reauth-title">
                            Sin autorización — contacte al administrador
                        </div>
                        <form
                            className="signage-display-reauth-form"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                if (!deviceTokenInput.trim()) return;
                                const tok = await exchangeDeviceToken(deviceTokenInput.trim());
                                if (tok) {
                                    setAuthState(AuthState.OK);
                                    setDeviceTokenInput('');
                                } else {
                                    setDeviceTokenInput('');
                                    alert('Token inválido o expirado.');
                                }
                            }}
                        >
                            <input
                                type="text"
                                placeholder="Pegue aquí un nuevo enlace de dispositivo"
                                value={deviceTokenInput}
                                onChange={(e) => setDeviceTokenInput(e.target.value)}
                                className="signage-display-reauth-input"
                            />
                            <button type="submit" className="signage-display-reauth-button">
                                Activar
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    if (!activeCampaign) {
        return (
            <div className="signage-display-root">
                <div className="signage-display-empty">
                    <DynamicSunLogo size={200} />
                </div>
            </div>
        );
    }

    return (
        <div className="signage-display-root">
            <CampaignPlayer
                key={`${activeCampaign.id}:${(activeCampaign.slides || []).length}`}
                campaign={activeCampaign}
                loop={true}
                onExit={() => { /* no-op; back is hidden by FullPageOS */ }}
            />
        </div>
    );
}

export default SignageDisplayPage;
